import {
  Badge,
  BodyText,
  Callout,
  Collapsible,
  Skeleton,
} from "@/components/ui";
import { useEncounter } from "@/network/useEncounter";
import { useParams, useSearch } from "@tanstack/react-router";

import { ErrorBoundary } from "@/lib/monitoring";
import { TriangleAlert } from "lucide-react";
import { VisitSummary } from "./VisitHistory";
import { useAmazonVisitHistory } from "./useAmazonVisitHistory";

export const AmazonVisitHistory = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: encounter } = useEncounter(
    encounterId as string,
    patientId as string,
  );

  const { data, isLoading, isError, refetch } = useAmazonVisitHistory({
    patientId: patientId as string,
    enabled: Boolean(encounter?.amazonId),
  });
  const visitSummaries = data?.treatmentPlanSummaries ?? [];

  if (!encounter?.amazonId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="grid gap-2">
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <Callout
        variant="danger"
        title="Failed to load visit history!"
        description="Please click here to try again."
        onClick={() => refetch()}
      />
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <Callout
          variant="danger"
          title="An error has occurred loading the visit history"
          description="Please refresh the page and try again. If the problem persists, please log a bug using the feedback tool."
        />
      }
    >
      <Collapsible
        title="Amazon One Medical Pay-Per-Visit History"
        headerSize="sm"
        defaultOpen
        statusBadge={
          <Badge variant="warning">
            <TriangleAlert className="h-3 w-3 text-warning" />
            <BodyText textColor="warning" size="sm">
              {visitSummaries?.length
                ? "Not Part of Medical Record"
                : "No Previous Visits Retrieved"}
            </BodyText>
          </Badge>
        }
      >
        {!visitSummaries.length ? (
          <BodyText textColor="tertiary" size="sm">
            No visit history found
          </BodyText>
        ) : (
          visitSummaries.map((summary, index) => (
            <VisitSummary
              key={
                summary.treatmentPlanDateTime ??
                `${summary.providerName}-${index}`
              }
              summary={summary}
            />
          ))
        )}
      </Collapsible>
    </ErrorBoundary>
  );
};
